import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import { dayKeyOf } from '../../utils/appDate';
import { API_DATE } from './expensesApi';

/**
 * The expenses screen's state.
 *
 * The rows themselves are NOT cached across visits the way the menu or the
 * customer book is. An expense list is always "what was spent between these
 * two dates", and the phone refetches on every entry for the same reason: an
 * expense entered on another till a minute ago has to be here. The date range
 * lives in the store so it survives a trip to another tab and back.
 *
 * Categories and vendors are cached, because they are short lists that change
 * rarely and are needed the moment the Add dialog opens.
 *
 * Everything here is wiped on logout by the reset in store.js.
 */

/** Month to date — the period an expense book is normally read for. */
const monthToDate = () => ({
  start: moment().startOf('month').format(API_DATE),
  end: moment().format(API_DATE),
});

const initialState = {
  range: monthToDate(),

  expenses: [],
  isExpensesDataFetched: false,

  expenseTypes: [],
  isExpenseTypesDataFetched: false,

  vendors: [],
  isVendorsDataFetched: false,
};

const expenseSlice = createSlice({
  name: 'expenses',
  initialState,
  reducers: {
    rangeChanged: (state, action) => {
      state.range = action.payload;
      // The rows on screen belong to the old window, so they are no longer
      // an answer to the question being asked.
      state.isExpensesDataFetched = false;
    },

    setExpenses: (state, action) => {
      state.expenses = action.payload || [];
    },

    setExpensesDataFetched: (state, action) => {
      state.isExpensesDataFetched = action.payload;
    },

    /**
     * A new expense, put straight at the top rather than refetched.
     *
     * It is only kept if it falls inside the window on screen — saving an
     * expense dated last month while looking at this month must not make a
     * row appear that a refresh would then remove.
     */
    addExpense: (state, action) => {
      const expense = action.payload;
      if (!expense) return;

      const day = dayKeyOf(expense.date);
      if (day < state.range.start || day > state.range.end) return;

      state.expenses.unshift(expense);
    },

    updateExpense: (state, action) => {
      const { _id, updated } = action.payload;
      const index = state.expenses.findIndex((row) => row._id === _id);
      if (index === -1) return;

      const merged = { ...state.expenses[index], ...updated };

      // An edit can move an expense out of the window being viewed. Dropping
      // it is what a refetch would do.
      const day = dayKeyOf(merged.date);
      if (day < state.range.start || day > state.range.end) {
        state.expenses.splice(index, 1);
        return;
      }

      state.expenses[index] = merged;
    },

    removeExpense: (state, action) => {
      state.expenses = state.expenses.filter((row) => row._id !== action.payload);
    },

    /* ── categories ── */

    setExpenseTypes: (state, action) => {
      state.expenseTypes = action.payload || [];
    },

    setExpenseTypesDataFetched: (state, action) => {
      state.isExpenseTypesDataFetched = action.payload;
    },

    addExpenseType: (state, action) => {
      if (action.payload) state.expenseTypes.push(action.payload);
    },

    updateExpenseType: (state, action) => {
      const { _id, updated } = action.payload;
      const index = state.expenseTypes.findIndex((row) => row._id === _id);
      if (index !== -1) {
        state.expenseTypes[index] = { ...state.expenseTypes[index], ...updated };
      }
    },

    removeExpenseType: (state, action) => {
      state.expenseTypes = state.expenseTypes.filter((row) => row._id !== action.payload);
    },

    /* ── vendors ── */

    setVendors: (state, action) => {
      state.vendors = action.payload || [];
    },

    setVendorsDataFetched: (state, action) => {
      state.isVendorsDataFetched = action.payload;
    },
  },
});

export const {
  rangeChanged,
  setExpenses,
  setExpensesDataFetched,
  addExpense,
  updateExpense,
  removeExpense,
  setExpenseTypes,
  setExpenseTypesDataFetched,
  addExpenseType,
  updateExpenseType,
  removeExpenseType,
  setVendors,
  setVendorsDataFetched,
} = expenseSlice.actions;

export default expenseSlice.reducer;
